import { useState, useMemo, useCallback } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { analyticsApi } from '../services/analytics-api.service';
import type {
  SPClientsQueryParams,
  SortOrder,
} from '../types/analytics.types';

const DEBOUNCE_MS = 300;

export const spClientsKeys = {
  all: ['analytics', 'sp-clients'] as const,
  list: (params?: Omit<SPClientsQueryParams, 'cursor'>) =>
    [...spClientsKeys.all, 'list', params] as const,
};

export const useSPClientsData = (
  baseParams?: Pick<SPClientsQueryParams, 'serviceCategoryId' | 'stateCode'>,
  enabled = true,
) => {
  const [search, setSearchRaw] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [sortBy, setSortBy] = useState<string>('totalClients');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const setSearch = useCallback(
    (value: string) => {
      setSearchRaw(value);
      if (debounceTimer) clearTimeout(debounceTimer);
      const timer = setTimeout(() => setDebouncedSearch(value), DEBOUNCE_MS);
      setDebounceTimer(timer);
    },
    [debounceTimer],
  );

  const queryParams = useMemo<Omit<SPClientsQueryParams, 'cursor'>>(() => {
    const params: Omit<SPClientsQueryParams, 'cursor'> = {
      sortBy,
      sortOrder,
      limit: 50,
    };
    if (debouncedSearch) params.search = debouncedSearch;
    if (baseParams?.serviceCategoryId)
      params.serviceCategoryId = baseParams.serviceCategoryId;
    if (baseParams?.stateCode) params.stateCode = baseParams.stateCode;
    return params;
  }, [sortBy, sortOrder, debouncedSearch, baseParams?.serviceCategoryId, baseParams?.stateCode]);

  const query = useInfiniteQuery({
    queryKey: spClientsKeys.list(queryParams),
    queryFn: async ({ pageParam }) => {
      const response = await analyticsApi.getSPClients({
        ...queryParams,
        cursor: pageParam ?? undefined,
      });
      return response.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 30 * 60 * 1000,
    enabled,
  });

  const allProviders = useMemo(
    () => query.data?.pages.flatMap((page) => page.providers) ?? [],
    [query.data?.pages],
  );

  const totalCount = query.data?.pages[0]?.totalCount ?? 0;

  const handleSort = useCallback(
    (column: string) => {
      if (column === sortBy) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(column);
        setSortOrder('desc');
      }
    },
    [sortBy],
  );

  return {
    providers: allProviders,
    totalCount,
    search,
    setSearch,
    sortBy,
    sortOrder,
    handleSort,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
